/**
 * CommunityPage — "/marketplace" (mockup: "Community").
 * Explore / Agents / Skills / Templates / Integrations, all backed by
 * existing endpoints (customAgentAPI marketplace + templates, the built-in
 * skill catalog, mcpAPI) — no new backend surface.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Bot, Loader2, Check, Plug,
  FlaskConical, PenLine, BarChart3, User, Code2, Lightbulb, Sparkles, Settings2, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { customAgentAPI, mcpAPI } from '../services/api.js';
import { SKILL_LIBRARY } from '../components/agentWorkspace/workspaceData.ts';

const T = {
  page: 'var(--lyra-color-surface)',
  surface: 'var(--lyra-color-surface)',
  surface2: 'var(--lyra-color-surface-2)',
  border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)',
  accentLight: 'var(--app-primary-soft)',
  accentMid: 'var(--app-primary-soft-2)',
  accentDark: 'var(--app-primary-deep)',
  green: 'var(--lyra-color-success)',
  greenBg: 'var(--lyra-color-success-bg)',
};

const TABS = ['explore', 'agents', 'skills', 'templates', 'integrations'] as const;
type Tab = typeof TABS[number];
const TAB_LABEL: Record<Tab, string> = { explore: 'Explore', agents: 'Agents', skills: 'Skills', templates: 'Templates', integrations: 'Integrations' };

function agentColors(item: any) {
  const text = `${item.name || ''} ${item.description || ''} ${item.category || ''}`.toLowerCase();
  if (/email|gmail|\bmail\b/.test(text)) return { bg: 'var(--lyra-color-danger-bg)', color: 'var(--lyra-color-danger)', Icon: Package };
  if (/linkedin|social|content|writ|post/.test(text)) return { bg: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', Icon: PenLine };
  if (/data|analy|report|chart/.test(text)) return { bg: 'var(--lyra-color-success-bg)', color: 'var(--lyra-color-success)', Icon: BarChart3 };
  if (/\bqa\b|test|quality/.test(text)) return { bg: 'var(--lyra-color-success-bg)', color: 'var(--lyra-color-primary)', Icon: FlaskConical };
  if (/personal|assist|plan/.test(text)) return { bg: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', Icon: User };
  if (/\bcode\b|develop|engineer/.test(text)) return { bg: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', Icon: Code2 };
  if (/product|roadmap|strategy/.test(text)) return { bg: 'var(--lyra-color-warning-bg)', color: 'var(--lyra-color-warning)', Icon: Lightbulb };
  if (/research|search|market|competitor/.test(text)) return { bg: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', Icon: Search };
  return { bg: T.accentLight, color: T.accent, Icon: Bot };
}

function fmtUses(n: number) {
  if (!n) return '0 uses';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k uses`;
  return `${n} use${n === 1 ? '' : 's'}`;
}

function AgentTile({ item, added, onAdd }: { item: any; added: boolean; onAdd: () => void }) {
  const { bg, color, Icon } = agentColors(item);
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={17} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.45, minHeight: 34, marginBottom: 12, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {item.description || 'No description'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, color: T.ink3 }}>by {item.author || 'Community'}</div>
          <div style={{ fontSize: 11, color: T.ink4 }}>{fmtUses(item.cloneCount || 0)}</div>
        </div>
        <button
          onClick={onAdd}
          disabled={added}
          style={{
            border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 600,
            cursor: added ? 'default' : 'pointer',
            background: added ? T.greenBg : T.accentDark,
            color: added ? T.green : 'var(--lyra-color-surface)',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          {added ? <><Check size={12} /> Added</> : 'Use'}
        </button>
      </div>
    </div>
  );
}

export default function CommunityPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('explore');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const [marketplace, setMarketplace] = useState<any[]>([]);
  const [loadingMarket, setLoadingMarket] = useState(true);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  const [mcpCount, setMcpCount] = useState<number | null>(null);

  useEffect(() => {
    customAgentAPI.getMarketplace().then((res: any) => {
      const list = res?.agents || [];
      setMarketplace(list);
      setAddedIds(new Set(list.filter((a: any) => a.alreadyAdded).map((a: any) => a._id)));
    }).catch(() => setMarketplace([])).finally(() => setLoadingMarket(false));

    customAgentAPI.getTemplates().then((res: any) => setTemplates(res?.templates || [])).catch(() => setTemplates([])).finally(() => setLoadingTemplates(false));

    mcpAPI.listServers().then((r: any) => setMcpCount((r?.servers || []).length)).catch(() => setMcpCount(null));
  }, []);

  const handleAdd = async (item: any) => {
    if (addedIds.has(item._id)) return;
    try {
      await customAgentAPI.addFromMarketplace(item._id);
      setAddedIds((prev) => new Set([...prev, item._id]));
      toast.success(`"${item.name}" added to your AI Team`);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setAddedIds((prev) => new Set([...prev, item._id]));
        toast('You already have this agent');
      } else {
        toast.error(err?.response?.data?.error || 'Failed to add agent');
      }
    }
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    marketplace.forEach((a) => a.category && set.add(a.category));
    return Array.from(set);
  }, [marketplace]);

  const filteredMarket = useMemo(() => marketplace.filter((a) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || a.name.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q);
    const matchesCategory = category === 'all' || a.category === category;
    return matchesSearch && matchesCategory;
  }), [marketplace, search, category]);

  const featured = useMemo(
    () => [...marketplace].sort((a, b) => (b.cloneCount || 0) - (a.cloneCount || 0)).slice(0, 4),
    [marketplace],
  );

  const filteredTemplates = useMemo(() => templates.filter((t) => {
    const q = search.toLowerCase();
    return !q || t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
  }), [templates, search]);

  const applyTemplate = (tmpl: any) => {
    navigate('/agent-builder/new', {
      state: {
        prefill: {
          name: tmpl.name,
          description: tmpl.description,
          model: tmpl.model || tmpl.defaultModel || '',
          systemPrompt: tmpl.systemPrompt || tmpl.prompt || '',
          examples: tmpl.examples || [],
          icon: tmpl.icon || '🤖',
        },
      },
    });
  };

  const filteredSkills = useMemo(() => SKILL_LIBRARY.filter((s: any) => {
    const q = search.toLowerCase();
    return !q || s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q);
  }), [search]);

  return (
    <div style={{ minHeight: '100vh', background: T.page, fontFamily: 'DM Sans, system-ui, sans-serif' }}>
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 32px 60px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Community</div>
          <div style={{ fontSize: 13, color: T.ink3 }}>Discover, use and share AI agents, skills and templates.</div>
        </div>
        <button
          onClick={() => navigate('/agents')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9, background: T.accent, color: 'var(--app-on-accent)', border: 'none', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', boxShadow: '0 2px 8px undefined' }}
        >
          <Sparkles size={14} /> Share to community
        </button>
      </div>

      {/* Tabs + search — the active indicator is its own line under the tab
          buttons, not the row's shared border, so it can't get visually
          swallowed by it. flex-end keeps the tabs bottom-aligned with the
          search/filter controls so that line sits exactly on the baseline. */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap', paddingBottom: 10 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 1, background: T.border }} />
        <div style={{ display: 'flex', gap: 18 }}>
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              style={{
                position: 'relative', border: 'none', background: 'none', padding: '4px 0 8px', fontSize: 13,
                fontWeight: tab === t ? 700 : 600, cursor: 'pointer', color: tab === t ? T.accent : T.ink3,
                borderBottom: tab === t ? `2.5px solid ${T.accent}` : '2.5px solid transparent',
              }}>
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
        {tab !== 'integrations' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: '7px 12px', width: 240 }}>
              <Search size={13} color={T.ink4} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search community…"
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 12.5, color: T.ink, width: '100%', fontFamily: 'inherit' }} />
            </div>
            {tab !== 'skills' && tab !== 'templates' && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowFilters((v) => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, background: T.surface, borderRadius: 9, padding: '7px 12px', fontSize: 12.5, color: T.ink2, cursor: 'pointer' }}>
                  <Filter size={13} /> Filters
                </button>
                {showFilters && (
                  <div style={{ position: 'absolute', top: '110%', right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 8, zIndex: 5, minWidth: 160, boxShadow: '0 8px 24px rgba(0,0,0,.08)' }}>
                    {['all', ...categories].map((c) => (
                      <button key={c} onClick={() => { setCategory(c); setShowFilters(false); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left', border: 'none', background: category === c ? T.accentLight : 'transparent',
                          color: category === c ? T.accentDark : T.ink2, borderRadius: 7, padding: '6px 10px', fontSize: 12, cursor: 'pointer', textTransform: 'capitalize',
                        }}>
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {tab === 'explore' && (
        <div>
          <div style={{
            background: 'linear-gradient(135deg, var(--lyra-color-surface-2) 0%, var(--app-surface-3) 100%)', border: `1px solid ${T.border}`, borderRadius: 18,
            padding: '36px 32px', marginBottom: 28, position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: T.ink, lineHeight: 1.25, marginBottom: 8, maxWidth: 420 }}>Get inspired.<br />Build together.</div>
            <div style={{ fontSize: 13, color: T.ink3, marginBottom: 18, maxWidth: 400 }}>
              Explore ready-to-use agents and templates from the Lyra community, or share your own.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--lyra-color-surface)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', maxWidth: 400 }}>
              <Search size={14} color={T.ink4} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search for agents, skills, or templates…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: T.ink, width: '100%', fontFamily: 'inherit' }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Featured agents</div>
            <button onClick={() => setTab('agents')} style={{ border: 'none', background: 'none', color: T.accentDark, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>View all →</button>
          </div>
          {loadingMarket ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '30px 0', color: T.ink3 }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
          ) : featured.length === 0 ? (
            <div style={{ fontSize: 12.5, color: T.ink4 }}>No community agents published yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
              {featured.map((item) => <AgentTile key={item._id} item={item} added={addedIds.has(item._id)} onAdd={() => handleAdd(item)} />)}
            </div>
          )}
        </div>
      )}

      {tab === 'agents' && (
        loadingMarket ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '40px 0', color: T.ink3 }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
        ) : filteredMarket.length === 0 ? (
          <div style={{ fontSize: 12.5, color: T.ink4, textAlign: 'center', padding: '40px 0' }}>No agents match.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
            {filteredMarket.map((item) => <AgentTile key={item._id} item={item} added={addedIds.has(item._id)} onAdd={() => handleAdd(item)} />)}
          </div>
        )
      )}

      {tab === 'skills' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
          {filteredSkills.slice(0, 60).map((s: any) => (
            <div key={s.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: T.accentLight, color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Settings2 size={15} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
              </div>
              <div style={{ fontSize: 11.5, color: T.ink3, lineHeight: 1.4 }}>{s.description}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'templates' && (
        loadingTemplates ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '40px 0', color: T.ink3 }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
        ) : filteredTemplates.length === 0 ? (
          <div style={{ fontSize: 12.5, color: T.ink4, textAlign: 'center', padding: '40px 0' }}>No templates match.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
            {filteredTemplates.map((t: any, i: number) => (
              <div key={t._id || t.id || i} onClick={() => applyTemplate(t)} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: T.accentLight, color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Bot size={15} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{t.name}</div>
                </div>
                <div style={{ fontSize: 11.5, color: T.ink3, lineHeight: 1.4, marginBottom: 12, minHeight: 30 }}>{t.description}</div>
                <div style={{ fontSize: 11.5, color: T.accentDark, fontWeight: 600 }}>Use template →</div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'integrations' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, textAlign: 'center' }}>
          <Plug size={26} color={T.ink3} style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
            {mcpCount == null ? 'Integrations' : `${mcpCount} integration${mcpCount === 1 ? '' : 's'} connected`}
          </div>
          <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 18 }}>Connect and manage MCP servers and integrations for your agents.</div>
          <button onClick={() => navigate('/integrations')}
            style={{ border: 'none', borderRadius: 10, padding: '10px 22px', background: T.accentDark, color: 'var(--app-on-accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Manage integrations
          </button>
        </div>
      )}

      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
    </div>
  );
}
